import { Autocomplete, Box, Button, CircularProgress, Divider, Grid, LinearProgress, Paper, Stack, TextField, Typography } from "@mui/material";
import Header from "components/Header";
import NoRecords from "components/NoRecords";
import { BasePathProvider, useBasePath } from "components/Providers";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import { useDispatch, useSelector } from "react-redux";
import {
  BeachAccessOutlined,
  EventBusyOutlined,
  HourglassEmptyOutlined,
  MoreTimeOutlined,
  SearchOffTwoTone,
} from '@mui/icons-material';
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { save, saveSelectedStaff } from "store/leave/punch-records"
import * as yup from "yup"
import { hasPermission } from "store"
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as snackbar from '../../../../utils/snackbar'

dayjs.extend(utc);

const STATUS_COLORS = {
  approved: { bg: '#e8f5e9', color: '#2e7d32', label: 'Approved' },
  pending: { bg: '#fff8e1', color: '#f57f17', label: 'Pending' },
  rejected: { bg: '#ffebee', color: '#c62828', label: 'Rejected' },
}

function ContextRow({ icon: Icon, label, value, status }) {
  const palette = STATUS_COLORS[status]
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.75 }}>
      <Icon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>{label}</Typography>
      {palette ? (
        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: palette.bg }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: palette.color }}>{value || palette.label}</Typography>
        </Box>
      ) : (
        <Typography variant="body2" fontWeight={500}>{value}</Typography>
      )}
    </Stack>
  )
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.75 }}>
      <Box sx={{ width: 18, flexShrink: 0 }} />
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>{label}</Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Stack>
  )
}

function ContextSection({ title, children }) {
  return (
    <Paper
      elevation={0}
      sx={{ mx: 2, mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default' }}
    >
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {title}
      </Typography>
      <Divider sx={{ my: 1 }} />
      {children}
    </Paper>
  )
}

const DayContextPanel = ({ data }) => {
  const { t } = useTranslation()
  if (!data) return null

  const hasLeaveContext = data.isHoliday || data.leaveRequest
  const hasPunchContext = data.punchPermission || data.manualPunch
  if (!hasLeaveContext && !hasPunchContext) return null

  return (
    <>
      {hasLeaveContext && (
        <ContextSection title={t('Leave Request Details')}>
          {data.isHoliday && (
            <ContextRow icon={BeachAccessOutlined} label={t('Holiday')} value={data.holidayName || t('Holiday')} />
          )}

          {data.leaveRequest && (
            <>
              <ContextRow
                icon={data.leaveRequest.status === 'approved' ? EventBusyOutlined : HourglassEmptyOutlined}
                label={t('Leave Type')}
                value={data.leaveRequest.typeName}
                status={data.leaveRequest.status}
              />
              <InfoRow label={t('Date Requested')} value={dayjs(data.leaveRequest.data.requestedDate).format('MMMM D, YYYY')} />
              {data.leaveRequest.data.note && <InfoRow label={t('Note')} value={data.leaveRequest.data.note} />}
            </>
          )}
        </ContextSection>
      )}

      {hasPunchContext && (
        <ContextSection title={t('Punch Request Details')}>

          {data.manualPunch?.data?.type && (
            <ContextRow
              icon={MoreTimeOutlined}
              label={t('Punch Type')}
              value={data.manualPunch.data.type.toUpperCase()}
            />
          )}
          {data.manualPunch?.data?.time && (
            <ContextRow
              icon={MoreTimeOutlined}
              label={t('Punch Time')}
              value={data.manualPunch.data.time}
            />
          )}
          {data.manualPunch?.data?.status && (
            <ContextRow
              icon={MoreTimeOutlined}
              label={t('Punch Status')}
              status={data.manualPunch.data.status}
            />
          )}
          {data.manualPunch?.data?.note && (
            <ContextRow
              icon={MoreTimeOutlined}
              label={t('Punch Note')}
              value={data.manualPunch.data.note}
            />
          )}
        </ContextSection>
      )}
    </>
  )
}

const schema = yup.object({
  eventId: yup.array().of(
    yup.mixed().nullable().required('Type is required')
  ),
})

export default function PunchRecordForm({ recordDetail, date, data, personId }) {
  const { t } = useTranslation()
  const basePath = useBasePath()
  const navigate = useNavigate()
  const types = useSelector(state => state.lookup.types)?.filter(type => type.category === 'punchRequestType' && type.name !== 'Permission')
  const dispatch = useDispatch()
  const isAdmin = hasPermission('hr.leave') || hasPermission('leave.punch')

  const { control, reset, formState, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { eventId: [] },
  });

  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && recordDetail?.dailyPunchRecords && types?.length) {
      const mappedEventIds = recordDetail.dailyPunchRecords.map(detail =>
        types.find(type => type.name.toUpperCase() === detail.EVENTID)
      );
      reset({ eventId: mappedEventIds });
      initialized.current = true;
    }
  }, [recordDetail, types, reset]);

  const onSubmit = async (formData) => {
    const { eventId } = formData

    try {
      for (const [index, detail] of recordDetail.dailyPunchRecords.entries()) {
        const newEvent = eventId[index]
        if (!newEvent) continue
        if (newEvent.name.toUpperCase() === detail.EVENTID) continue
        const action = isAdmin
          ? saveSelectedStaff({ personId, id: detail.Id, eventId: newEvent.code })
          : save({ id: detail.Id, eventId: newEvent.code })
        await dispatch(action).unwrap()
      }
      snackbar.success('Successfully Saved')
      navigate(basePath)
    } catch (err) {
      snackbar.error('Error Saving! Please try again later')
    }
  }

  return (
    <BasePathProvider value={`${basePath}/punch-records`}>
      <Header title={`${t('Punch Records')} — ${dayjs(date).format('MMMM D, YYYY')}`} small close borderBottom />

      <DayContextPanel data={data} />

      <Grid container p={2} component="form" onSubmit={handleSubmit(onSubmit)} spacing={0}>
        {recordDetail?.dailyPunchRecords?.map((detail, index) => (
          <Grid item xs={12} key={index}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={2}>
                <Controller
                  name={`eventId.${index}`}
                  control={control}
                  render={({ field: { onChange, value, ref }, fieldState }) => (
                    <Autocomplete
                      options={types}
                      getOptionLabel={option => option?.name || ''}
                      isOptionEqualToValue={(option, val) => option?.name === val?.name}
                      value={value || null}
                      onChange={(_, newValue) => onChange(newValue)}
                      disableClearable
                      renderInput={params => (
                        <TextField
                          {...params}
                          inputRef={ref}
                          label={`${t('Record')} ${index + 1} ${t('Type')}`}
                          fullWidth
                          margin="normal"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={`${t('Record')} ${index + 1} ${t('Time')}`}
                  value={dayjs.utc(detail?.LOCAL_TIMESTAMP).format('hh:mm:ss A')}
                  fullWidth margin="normal" disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={`${t('Record')} ${index + 1} ${t('Terminal')}`}
                  value={detail?.TERMINALSN}
                  fullWidth margin="normal" disabled
                />
              </Grid>
            </Grid>
          </Grid>
        ))}

        {!recordDetail && (
          <Grid item xs={12}>
            <LinearProgress sx={{ width: '100%' }} />
          </Grid>
        )}

        {recordDetail && !recordDetail.dailyPunchRecords?.length && (
          <Grid item xs={12}>
            <NoRecords message={t('No punch records found for this date.')} icon={<SearchOffTwoTone sx={{ fontSize: 50, color: 'primary.main' }} />} />
          </Grid>
        )}

        {recordDetail && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={formState.isSubmitting || !formState.isDirty}
                startIcon={formState.isSubmitting && <CircularProgress size={20} />}
              >
                {t('Save')}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </BasePathProvider>
  )
}
